<template>
  <div data-testid="datepicker-root" :class="{ 'lb-date-picker--menu': menu }">
    <v-menu
      v-if="menu"
      v-model="menuOpen"
      :close-on-content-click="false"
      :attach="attach"
      offset-y
      nudge-bottom="4"
      min-width="auto"
      :z-index="menuZIndex"
      transition="scale-transition"
      content-class="lb-date-picker__menu"
      data-testid="datepicker-menu"
      @input="onMenuToggle"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          class="lb-date-picker__activator"
          v-bind="attrs"
          v-on="on"
          data-testid="datepicker-input-wrapper"
        >
          <v-text-field
            ref="element"
            type=""
            prepend-inner-icon="mdi-calendar-range"
            dense
            outlined
            readonly
            :label="effectiveLabel"
            :clearable="clearable"
            :error-messages="error"
            class="noTransform lb-date-picker__field"
            :value="formattedRangevalue"
            :hide-details="hidedetails"
            :disabled="disabled"
            :placeholder="effectivePlaceholder"
            data-testid="datepicker-textfield"
            @click:clear.stop="onClear"
          />
        </div>
      </template>
      <v-date-picker
        v-model="tempContent"
        elevation="0"
        color="tableHeaderBg black--text"
        :min="min"
        :max="max"
        show-adjacent-months
        :type="type"
        :readonly="readonly"
        no-title
        scrollable
        data-testid="datepicker-calendar"
        @input="onMenuDateSelect"
      />
    </v-menu>

    <template v-else>
      <div @click.stop="dialog = true" data-testid="datepicker-input-wrapper">
        <v-text-field
          ref="element"
          type=""
          prepend-inner-icon="mdi-calendar-range"
          dense
          outlined
          :label="effectiveLabel"
          :clearable="clearable"
          :error-messages="error"
          class="noTransform"
          :value="formattedRangevalue"
          @keypress="dialog = true"
          @click:clear.stop="onClear"
          :hide-details="hidedetails"
          :disabled="disabled"
          :placeholder="effectivePlaceholder"
          data-testid="datepicker-textfield"
        />
      </div>
      <v-dialog max-width="290px" v-model="dialog" transition="dialog-transition" data-testid="datepicker-dialog">
        <v-card>
          <v-card-text class="pa-0">
            <v-date-picker
              elevation="0"
              v-model="tempContent"
              color="tableHeaderBg black--text"
              class=""
              :min="min"
              :max="max"
              show-adjacent-months
              :type="type"
              :readonly="readonly"
              data-testid="datepicker-calendar"
            >
              <v-spacer></v-spacer>
              <v-btn text @click="dialog = false" class="secondaryBtn" data-testid="datepicker-close-btn"><span>Close</span></v-btn>
              <v-btn
                elevation="0"
                color="primary"
                @click="applyDialogSelection"
                data-testid="datepicker-ok-btn"
              >
                <span>Ok</span>
              </v-btn>
            </v-date-picker>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
// /* eslint-disable vue/multi-word-component-names */
export default {
  name: 'DatePicker',
  activated() {
    this.setValue();
    this.updateValue();
  },
  created() {
    this.setValue();
    this.updateValue();
  },
  data: () => ({
    formattedvalue: '',
    dialog: false,
    menuOpen: false,
    content: '',
    tempContent: '',
  }),
  props: {
    error: {
      type: [String, Array],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    min: {
      type: String,
      default: undefined,
    },
    max: {
      type: String,
      default: undefined,
    },
    type: {
      type: String,
      default: 'date',
    },
    hidedetails: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * Show `label` only as an empty-state hint.
     * Once a date is selected, it is not floated into the outline.
     */
    labelAsHint: {
      type: Boolean,
      default: false,
    },
    /** When true, opens calendar in a dropdown menu instead of a dialog modal. */
    menu: {
      type: Boolean,
      default: false,
    },
    /** Attach menu to app root — use inside nested dialogs/drawers. */
    attach: {
      type: Boolean,
      default: false,
    },
    /** z-index for menu dropdown (raise inside modals). */
    menuZIndex: {
      type: Number,
      default: 10000,
    },
  },
  methods: {
    toPickerDate(value) {
      if (value == null || value === '') return '';
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      const raw = String(value).trim();
      const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
      const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (us) {
        return `${us[3]}-${String(us[1]).padStart(2, '0')}-${String(us[2]).padStart(2, '0')}`;
      }
      const fromNova = this.$nova.formatDateISO(raw);
      if (fromNova && /^\d{4}-\d{2}-\d{2}/.test(String(fromNova))) {
        return String(fromNova).slice(0, 10);
      }
      const parsed = new Date(raw);
      if (!Number.isNaN(parsed.getTime())) return this.toPickerDate(parsed);
      return '';
    },
    isValidRealisticDate(dateStr) {
      try {
        const iso = this.toPickerDate(dateStr);
        if (!iso) return false;
        if (this.type === 'month' && /^\d{4}-\d{2}$/.test(iso)) {
          const year = Number(iso.slice(0, 4));
          const month = Number(iso.slice(5, 7));
          return year >= 1900 && year <= 2200 && month >= 1 && month <= 12;
        }
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
        if (!match) return false;
        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        if (year < 1900 || year > 2200) return false;
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year
          && date.getMonth() === month - 1
          && date.getDate() === day;
      } catch (err) {
        return false;
      }
    },
    setValue() {
      const formattedDate = this.toPickerDate(this.value);

      if (this.isValidRealisticDate(formattedDate)) {
        this.content = formattedDate;
        this.tempContent = formattedDate;
      } else {
        this.content = '';
        this.tempContent = '';
      }

      this.updateValue();
    },
    updateValue() {
      this.$emit('input', this.content);
      this.$emit('change', this.content);
    },
    onClear() {
      this.content = '';
      this.tempContent = '';
      this.updateValue();
    },
    onMenuDateSelect(value) {
      this.tempContent = value;
      this.content = value;
      this.menuOpen = false;
      this.updateValue();
    },
    onMenuToggle(isOpen) {
      if (isOpen) {
        this.tempContent = this.content;
      }
    },
    applyDialogSelection() {
      this.content = this.tempContent;
      this.dialog = false;
      this.updateValue();
    },
    setFocus() {
      this.$nextTick(() => {
        this.$refs.element.$el.querySelector('input').focus();
      });
    },
    setBlank() {
      this.content = '';
      this.tempContent = '';
    },
  },
  computed: {
    effectiveLabel() {
      if (this.labelAsHint) return '';
      return this.label || 'Date';
    },
    effectivePlaceholder() {
      if (this.labelAsHint) return this.placeholder || this.label || 'Date';
      return this.placeholder;
    },
    formattedRangevalue() {
      if (!this.isValidRealisticDate(this.content)) {
        return '';
      }

      let format = undefined;
      if (this.type === 'month') format = 'MMM YYYY';
      const v = this.$nova.formatDate(this.content, format);
      if (v !== '' && v !== null && v !== undefined) return v;
      return '';
    },
  },
  watch: {
    value() {
      this.setValue();
    },
    content(newVal) {
      if (newVal && !this.isValidRealisticDate(newVal)) {
        this.setBlank();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lb-date-picker__activator {
  cursor: pointer;
}

.lb-date-picker--menu ::v-deep .lb-date-picker__field {
  pointer-events: none;
}

.lb-date-picker--menu ::v-deep .lb-date-picker__field .v-input__icon--clear {
  pointer-events: auto;
  cursor: pointer;
}
</style>

<style lang="scss">
.lb-date-picker__menu {
  z-index: 10000 !important;
  border-radius: var(--radius-sm, 8px) !important;
  overflow: hidden;
}
</style>
